<template>
  <MainStructure>
    <div class="logs-container">
      <div class="logs-menu">
        <div class="file-list">
          <div class="file-list-header">
            <h3 class="file-list-title">Simulador</h3>

            <div v-if="isDownloadingSimulatorLogs"
                 class="spinner-border"
                 role="status"/>

            <AppIcons v-else
                      class="file-list-download"
                      icon="filetype-csv"
                      size="30"
                      @click="downloadSimulatorLogs"/>
          </div>

          <div v-if="isLoadingSimulatorLogs" class="spinner-container">
            <div class="spinner-border"
                 role="status"/>
          </div>

          <div class="scroller-container"
               v-else>
            <RecycleScroller
                class="scroller"
                :items="simulatorLogs"
                :item-size="32"
                key-field="id">
              <template v-slot="{ item }">
                <div class="log"
                     :class="'file-list-li' + (item.active ? ' active' : '')"
                     :title="item.title"
                     @click="selectSimulatorLog(item)">
                  {{ item.title }}
                </div>
              </template>
            </RecycleScroller>
          </div>
        </div>

        <div class="file-list">
          <div class="file-list-header">
            <h3 class="file-list-title">Chat</h3>

            <div v-if="isDownloadingChatLogs"
                 class="spinner-border"
                 role="status"/>

            <AppIcons v-else
                      class="file-list-download"
                      icon="filetype-csv"
                      size="30"
                      @click="downloadChatLogs"/>
          </div>

          <div v-if="isLoadingChatLogs" class="spinner-container">
            <div class="spinner-border"
                 role="status"/>
          </div>

          <div class="scroller-container"
               v-else>
            <RecycleScroller
                class="scroller"
                :items="chatLogs"
                :item-size="32"
                key-field="session_id">
              <template v-slot="{ item }">
                <div class="log"
                     :class="'file-list-li' + (item.active ? ' active' : '')"
                     :title="getChatLogTitle(item)"
                     @click="selectChatLog(item)">
                  {{ getChatLogTitle(item) }}
                </div>
              </template>
            </RecycleScroller>
          </div>
        </div>
      </div>

      <div class="file-list-content">
        <div v-if="log">
          <div class="file-list-content-title">
            {{ getChatLogTitle(log) }}
          </div>

          <div v-html="getFormattedContent(log.content)"></div>
        </div>

        <p v-else>Selecione um log para visualizar o conteúdo.</p>
      </div>
    </div>
  </MainStructure>
</template>

<script setup>
import MainStructure from '@/components/layoutParts/MainStructure.vue';
import {onMounted, ref} from 'vue';

import {useLogsStore} from "@/stores/logs";
import {useRouter} from "vue-router";
import AppIcons from "@/components/layoutParts/AppIcons.vue";
import LogService from "@/services/log.service";

const router = useRouter();
const store = useLogsStore();
const log = ref(null);
const isLoadingSimulatorLogs = ref(true);
const isLoadingChatLogs = ref(true);
const isDownloadingSimulatorLogs = ref(false);
const isDownloadingChatLogs = ref(false);

const simulatorLogs = ref([]);
const chatLogs = ref([]);

const selectSimulatorLog = (selectedLog) => {
  LogService.getSimulatorLogContent(selectedLog.id)
      .then(response => {
        log.value = {...selectedLog};
        log.value.content = response.data;
        simulatorLogs.value.forEach((l) => l.active = l.id === selectedLog.id);
      });
}

const selectChatLog = (selectedLog) => {
  log.value = {...selectedLog};
  chatLogs.value.forEach((l) => l.active = l['session_id'] === selectedLog['session_id']);
}

const downloadSimulatorLogs = () => {
  isDownloadingSimulatorLogs.value = true;
  LogService.downloadSimulatorLogs()
      .finally(() => isDownloadingSimulatorLogs.value = false);
}

const downloadChatLogs = () => {
  isDownloadingChatLogs.value = true;
  LogService.downloadChatLogs()
      .finally(() => isDownloadingChatLogs.value = false);
}

const getChatLogTitle = (logToBeFormatted) => {
  if (logToBeFormatted['title']) {
    return logToBeFormatted['title'];
  }

  const date = new Date(logToBeFormatted['created_at']);
  return date.toLocaleString('pt-br') + ', Session ID ' + logToBeFormatted['session_id'] + ', Exercício ' + logToBeFormatted['exercise'] + ', Estudante ' + logToBeFormatted['student'];
}

const getFormattedContent = (content) => {
  if (typeof content === 'string') {
    return content;
  }

  return content.map(message => {
    const speaker = message.type === 'human' ? 'Estudante' : 'IA';
    return `<b>${speaker}</b>: ${message.content}`;
  }).join('\n\n');
}

const fetchSimulatorLogs = async () => {
  isLoadingSimulatorLogs.value = true;
  return LogService.getSimulatorLogs()
      .then(response => simulatorLogs.value = response.data)
      .finally(() => isLoadingSimulatorLogs.value = false);
}

const fetchChatLogs = async () => {
  isLoadingChatLogs.value = true;
  return LogService.getChatLogs()
      .then(response => chatLogs.value = response.data)
      .finally(() => isLoadingChatLogs.value = false);
}

onMounted(async () => {
  await store.init();

  if (!store.hasPermission) {
    await router.push({name: 'ListExercises'});
    return;
  }

  await fetchSimulatorLogs();
  await fetchChatLogs();
})

</script>

<style scoped>
.logs-container {
  display: flex;
  width: 100%;
  height: 100%;
}

.logs-menu {
  width: 25%;
  max-width: 300px;
  height: 100%;
}

.file-list {
  height: 50%;
  border-right: 1px solid #E8EFFB;
  border-top: 1px solid #E8EFFB;
  overflow-y: auto;
  color: #1e1e1e;
  padding: 10px;
}

.file-list-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.file-list-title {
  flex-grow: 1;
  margin-bottom: unset !important;
  color: var(--bs-secondary-color) !important;
}

.file-list-download {
  margin-right: 6px;
}

.file-list-download:hover {
  cursor: pointer;
  transform: scale(1.1);
  transition: .2s;
}

.file-list-ul {
  list-style: none;
}

.file-list-li {
  margin: 10px 0 10px 0;
  cursor: pointer;
  color: #212529;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.file-list-li:hover {
  background-color: #d7d8d9;
}

.file-list-li.active {
  background-color: #007acc;
  color: white;
}

.file-list-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  white-space: pre-wrap;
  color: #212529;
}

.file-list-content-title {
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.scroller-container {
  height: calc(100% - 50px);
}

.scroller {
  height: 100%;
}

.log {
  height: 32%;
  padding: 0 12px;
  align-items: center;
}

.spinner-container {
  height: calc(100% - 50px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.spinner-border {
  margin-right: 6px;
  color: #1A5FD5;
}
</style>
